// The chat overlay's client-side seam contract + hook (beekeeper-agent-chat, `client-beekeeper-chat`,
// `client-colony` "In rounds mode the prompt control opens the chat overlay rather than
// submitting"). Canonical home of the `ChatSeam` contract `Colony.tsx` types against —
// `app/rounds/chat-channel.ts`'s `createChatChannel` is the real implementation and satisfies this
// same shape structurally, mirroring `ui/colony/controller.ts`'s own "declared here as the seam's
// contract ahead of the real implementation" precedent (and `ui/colony/prompt-cooldown.ts`'s
// `useSyncExternalStore` hook shape, copied verbatim below).
//
// Undefined in live mode (and in a bare mount before the rounds composition root wires it): Colony
// renders no overlay and `PromptBox` falls back to its original one-shot-generation behaviour —
// the same "renders/behaves differently only once wired" convention `run`/`roundsReview` already
// established on this screen (client-beekeeper-chat "the overlay ... mounted only ... in rounds
// mode").

import { useMemo, useSyncExternalStore } from 'react';
import type { TranscriptState } from '../chat/transcript-model';

/**
 * The channel's own progress phase (`beekeeper-progress-indicator` §1) — finer-grained than
 * `transcript.running`, because "the POST is in flight but the server has not answered yet" and
 * "the server answered non-2xx and the outcome cannot be told apart from admitted" are both real,
 * distinct states a player-visible indicator needs, and neither is `running`.
 *
 * - `idle`: nothing outstanding.
 * - `submitting`: the POST is in flight, before any server frame — set synchronously with the
 *   send, which is the whole point (feedback before the network answers).
 * - `running`: a `turnStarted` event has been observed (live, or replayed on reattach).
 * - `uncertain`: a send-leg failure whose reason is not demonstrably pre-admission (a dropped
 *   connection, `not_admitting`, an unrecognised or unparseable refusal) — the outcome is
 *   genuinely unknown and resolves only on evidence, never on a timer.
 * - `failed`: a send-leg refusal whose reason is demonstrably pre-admission — nothing was
 *   admitted, so resending (editing and pressing Send again) is safe and is the retry mechanism.
 */
export type ChatPhase = 'idle' | 'submitting' | 'running' | 'uncertain' | 'failed';

/** Whether `phase` should read as "busy" to a caller gating on it — the composer, the mutation
 * gate, the header indicator. Deliberately a pure function of `phase` rather than a stored
 * snapshot field: there is nothing here a consumer could not compute itself, and storing it too
 * would just be a second place the two could disagree. */
export function isChatBusy(phase: ChatPhase): boolean {
  return phase === 'submitting' || phase === 'running' || phase === 'uncertain';
}

/**
 * The overlay's lifecycle (`player-chat-and-outcome` **D-2**) — three states where there used to be
 * an `open` boolean, because "the panel is away" and "the conversation is over" were the same fact
 * and a player could only have both or neither.
 *
 * - `closed`: no conversation on screen and none being returned to. The state a fresh channel
 *   starts in, and the one an explicit dismissal (a new match, a session reset) reaches.
 * - `open`: the overlay is mounted.
 * - `minimised`: the overlay's body is unmounted while **everything behind it keeps running** — the
 *   session, its SSE subscription and any in-flight turn. Only `closed` is ever allowed to tear
 *   anything down; minimising is a view change and nothing else, which is why {@link
 *   ChatSeam.minimise} is honoured mid-turn where {@link ChatSeam.close} is refused.
 *
 * `closed` and `minimised` render identically today (no overlay, a re-entry row): the distinction
 * is in what the transition is *permitted* to do, and it is named rather than inferred so a future
 * teardown cannot quietly attach itself to the panic key.
 */
export type ChatView = 'closed' | 'open' | 'minimised';

export interface ChatSeamSnapshot {
  view: ChatView;
  /**
   * The agent has produced output since the player last had the overlay open
   * (`player-chat-and-outcome` **D-3**) — what `ChatReentryRow` draws its halo from.
   *
   * **Set by arrival while away, cleared only by viewing.** Never by a timer: an indicator that
   * decays says "something happened recently", which is not a useful claim; one that clears when
   * the panel is opened says "unread", which is. That is a statement about the player, so only the
   * player's own act (opening the thread, or sending — which opens it) clears it.
   */
  unread: boolean;
  transcript: TranscriptState;
  phase: ChatPhase;
  /** Placeholder-only: true from the moment a send goes out until the first `textDelta`/
   * `assistantMessage`/`toolStarted` is observed for that turn (live or replayed). Rendered as
   * `isChatBusy(phase) && awaitingFirstOutput` — derived, never latched, so a turn that settles
   * (or fails) with no output ever having arrived cannot strand a "thinking" placeholder. */
  awaitingFirstOutput: boolean;
  /** True only alongside `phase === 'failed'`, and only for ONE specific cause
   * (`beekeeper-progress-indicator` review, H1): a refresh found this submission's turn already
   * settled with NO agent output at all — the server's post-write `not_admitting` path can settle
   * a turn `Stopped` with an empty log after `begin_turn` has already appended the player's
   * message. The gate handling is correct regardless (the turn genuinely settled, nothing holds
   * the sandbox); this flag exists only so a renderer can show an honest "that didn't reach the
   * Beekeeper" line instead of the generic refusal copy, which would otherwise claim nothing was
   * admitted when something plainly was. Absent (never `false`) for every other route into
   * `failed`, so a caller that does not care can simply treat it as falsy. */
  noOutputSettle?: boolean;
}

export interface ChatSeam {
  getSnapshot(): ChatSeamSnapshot;
  subscribe(listener: () => void): () => void;
  /** `PromptBox` Send: posts `text`, opens the overlay, and starts the turn's live stream. */
  sendAndOpen(text: string, targetType: string | null): void;
  /** The separate re-entry control: opens the overlay over the held transcript, sending nothing.
   * Clears {@link ChatSeamSnapshot.unread} — this is the player looking. */
  openExisting(): void;
  stop(): void;
  /**
   * Puts the panel away without ending anything (`player-chat-and-outcome` **D-2**) — what every
   * mount wires the overlay's ✕ and Escape to. Unconditional: unlike {@link close} it is honoured
   * mid-turn, because it stops nothing, closes no stream and cancels no subscription.
   */
  minimise(): void;
  /** Ends the conversation's presence on screen outright. Refuses while a turn is running (there is
   * a live turn whose only visible surface this is) — reach for {@link minimise} instead. */
  close(): void;
  rehydrate(): Promise<void>;
  /**
   * "New chat" — clears the conversation, server-side included, and starts a new one (WAG-94).
   *
   * **Optional, and its absence is the renderer's signal not to offer it.** A transport that cannot
   * clear — the presentation's read-only preview, which reaches only the two GET routes — produces
   * a seam without this method, so the control is absent rather than present-but-broken. A caller
   * therefore renders the button on `clearConversation !== undefined`, never unconditionally.
   *
   * A no-op while a turn is busy: the server refuses it there too (`turn_in_progress`), and the
   * transcript a running turn is still writing is not a conversation to throw away behind its back.
   */
  clearConversation?(): Promise<void>;
}

const NO_SUBSCRIBE = (): (() => void) => () => {};
const NO_SNAPSHOT = (): ChatSeamSnapshot | undefined => undefined;

/** Reads a {@link ChatSeam} as a re-rendering snapshot. `undefined` when no seam is injected
 * (live mode, or a bare mount before the rounds root wires one) — the caller reads that as "no
 * overlay exists to open" exactly as `useAdvisoryPromptCooldown`'s `null` reads as "no rounds
 * cooldown seam". */
export function useChatSeam(seam?: ChatSeam): ChatSeamSnapshot | undefined {
  const subscribe = useMemo(
    () => (seam === undefined ? NO_SUBSCRIBE : (onChange: () => void) => seam.subscribe(onChange)),
    [seam],
  );
  const snapshot = useMemo(() => (seam === undefined ? NO_SNAPSHOT : () => seam.getSnapshot()), [seam]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
